/**
 * useScheduleMutations.ts
 * Unifies the optimistic-edit path for the Machine Schedule (UX overhaul — M1).
 *
 * `commitPlacement` is the single funnel every placement edit goes through —
 * drag-resize, drag-move (incl. across machines), and keyboard nudge:
 *   1. apply the change to local state immediately (optimistic, no refetch),
 *   2. persist via PUT /bookings/:key (unless the caller already did),
 *   3. on failure, revert the local change and surface the error,
 *   4. quietly recompute conflicts,
 *   5. record an undo/redo entry and pop an "Undo" snackbar.
 *
 * History (undo/redo + Ctrl+Z/Y) is owned here so the page only wires buttons.
 */

import { useCallback, useState } from 'react';
import { updateBookingPlacement, deleteMachineBooking } from '../services/scheduleBookings';
import { emitScheduleChange } from '../services/scheduleBus';
import { useScheduleHistory } from './useScheduleHistory';
import type { MachineBookingCalendar } from '../types/schedule';
import type {
  BookingPlacementPatch,
  BookingPlacementSnapshot,
} from './useScheduleData';

export interface ScheduleToast {
  message: string;
  onUndo?: () => void;
}

interface UseScheduleMutationsArgs {
  applyLocalBookingChange: (
    key: string,
    patch: BookingPlacementPatch,
  ) => BookingPlacementSnapshot | null;
  removeLocalBooking: (key: string) => MachineBookingCalendar | null;
  restoreLocalBooking: (booking: MachineBookingCalendar) => void;
  reloadConflicts: () => Promise<void>;
  onToast: (toast: ScheduleToast) => void;
  onError: (message: string) => void;
  /** True while a drag gesture is active — suppresses keyboard undo/redo. */
  dragActive?: boolean;
}

export interface CommitOptions {
  /** The caller already persisted this change (legacy drag hook PUTs itself). */
  alreadyPersisted?: boolean;
  /** Suppress the "Undo" snackbar (e.g. for an undo/redo-triggered commit). */
  silent?: boolean;
}

/** One member of a batched placement change (BUG-2026-0193 merged prep bars). */
export interface PlacementBatchItem {
  key: string;
  patch: BookingPlacementPatch;
}

interface UseScheduleMutationsResult {
  commitPlacement: (
    key: string,
    patch: BookingPlacementPatch,
    label: string,
    opts?: CommitOptions,
  ) => Promise<void>;
  /**
   * BUG-2026-0193 — move several bookings as ONE edit (a merged prep bar's
   * members): optimistic for all, persisted one by one, reverted together on
   * failure, and recorded as a single undo/redo entry + one toast. A one-item
   * batch delegates to commitPlacement.
   */
  commitPlacementBatch: (items: PlacementBatchItem[], label: string) => Promise<void>;
  /** Optimistically delete a booking (soft-delete on the server). Not undoable. */
  commitDelete: (booking: MachineBookingCalendar, label: string) => Promise<void>;
  /** True while a persist call is in flight (pauses background auto-refresh). */
  isMutating: boolean;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

/** Keep only the fields present in `keys` from a snapshot, as a patch. */
function patchFromSnapshot(
  snapshot: BookingPlacementSnapshot,
  changed: BookingPlacementPatch,
): BookingPlacementPatch {
  const out: BookingPlacementPatch = {};
  if (changed.StartDate !== undefined) out.StartDate = snapshot.StartDate;
  if (changed.EndDate !== undefined) out.EndDate = snapshot.EndDate;
  if (changed.MachineKey !== undefined) out.MachineKey = snapshot.MachineKey;
  return out;
}

/** The forward patch reduced to only its defined fields. */
function definedPatch(patch: BookingPlacementPatch): BookingPlacementPatch {
  const out: BookingPlacementPatch = {};
  if (patch.StartDate !== undefined) out.StartDate = patch.StartDate;
  if (patch.EndDate !== undefined) out.EndDate = patch.EndDate;
  if (patch.MachineKey !== undefined) out.MachineKey = patch.MachineKey;
  return out;
}

export function useScheduleMutations({
  applyLocalBookingChange,
  removeLocalBooking,
  restoreLocalBooking,
  reloadConflicts,
  onToast,
  onError,
  dragActive,
}: UseScheduleMutationsArgs): UseScheduleMutationsResult {
  const history = useScheduleHistory({
    disabled: dragActive,
    onAfterUndo: (label) => onToast({ message: `Undone: ${label}` }),
    onAfterRedo: (label) => onToast({ message: `Redone: ${label}` }),
  });

  // Count of in-flight persist calls (so background refresh can pause).
  const [pending, setPending] = useState(0);

  const commitPlacement = useCallback(
    async (
      key: string,
      patch: BookingPlacementPatch,
      label: string,
      opts: CommitOptions = {},
    ) => {
      const forward = definedPatch(patch);
      const before = applyLocalBookingChange(key, forward);
      if (!before) return; // unknown key — nothing to do
      const inverse = patchFromSnapshot(before, forward);

      if (!opts.alreadyPersisted) {
        setPending((p) => p + 1);
        try {
          await updateBookingPlacement(key, forward);
        } catch (err) {
          applyLocalBookingChange(key, inverse); // revert optimistic change
          onError(err instanceof Error ? err.message : 'Failed to update booking.');
          return;
        } finally {
          setPending((p) => p - 1);
        }
      }
      await reloadConflicts();
      // Tell the other views (Employee Schedule, Scenario Planning) to quietly
      // revalidate — the booking move re-spanned its employee staffing on the server.
      emitScheduleChange({ kind: 'bookings-changed', source: 'machine-schedule' });

      history.record({
        label,
        undo: async () => {
          applyLocalBookingChange(key, inverse);
          try { await updateBookingPlacement(key, inverse); } catch { /* best-effort */ }
          await reloadConflicts();
          emitScheduleChange({ kind: 'bookings-changed', source: 'machine-schedule' });
        },
        redo: async () => {
          applyLocalBookingChange(key, forward);
          try { await updateBookingPlacement(key, forward); } catch { /* best-effort */ }
          await reloadConflicts();
          emitScheduleChange({ kind: 'bookings-changed', source: 'machine-schedule' });
        },
      });

      if (!opts.silent) {
        onToast({ message: label, onUndo: () => history.undo() });
      }
    },
    [applyLocalBookingChange, reloadConflicts, onToast, onError, history],
  );

  const commitPlacementBatch = useCallback(
    async (items: PlacementBatchItem[], label: string) => {
      if (items.length === 1) return commitPlacement(items[0].key, items[0].patch, label);
      const applied: Array<{ key: string; forward: BookingPlacementPatch; inverse: BookingPlacementPatch }> = [];
      for (const it of items) {
        const forward = definedPatch(it.patch);
        if (Object.keys(forward).length === 0) continue;
        const before = applyLocalBookingChange(it.key, forward);
        if (!before) continue; // unknown key — skip
        applied.push({ key: it.key, forward, inverse: patchFromSnapshot(before, forward) });
      }
      if (applied.length === 0) return;

      const persistAll = async (dir: 'forward' | 'inverse') => {
        for (const a of applied) await updateBookingPlacement(a.key, a[dir]);
      };
      const applyAll = (dir: 'forward' | 'inverse') => {
        for (const a of applied) applyLocalBookingChange(a.key, a[dir]);
      };

      setPending((p) => p + 1);
      const persisted: typeof applied = [];
      try {
        for (const a of applied) {
          await updateBookingPlacement(a.key, a.forward);
          persisted.push(a);
        }
      } catch (err) {
        // Revert the optimistic change everywhere and best-effort roll back the
        // members already persisted so the group never ends up half-moved.
        applyAll('inverse');
        for (const a of persisted) {
          try { await updateBookingPlacement(a.key, a.inverse); } catch { /* best-effort */ }
        }
        onError(err instanceof Error ? err.message : 'Failed to update bookings.');
        return;
      } finally {
        setPending((p) => p - 1);
      }
      await reloadConflicts();
      emitScheduleChange({ kind: 'bookings-changed', source: 'machine-schedule' });

      history.record({
        label,
        undo: async () => {
          applyAll('inverse');
          try { await persistAll('inverse'); } catch { /* best-effort */ }
          await reloadConflicts();
          emitScheduleChange({ kind: 'bookings-changed', source: 'machine-schedule' });
        },
        redo: async () => {
          applyAll('forward');
          try { await persistAll('forward'); } catch { /* best-effort */ }
          await reloadConflicts();
          emitScheduleChange({ kind: 'bookings-changed', source: 'machine-schedule' });
        },
      });
      onToast({ message: label, onUndo: () => history.undo() });
    },
    [commitPlacement, applyLocalBookingChange, reloadConflicts, onToast, onError, history],
  );

  const commitDelete = useCallback(
    async (booking: MachineBookingCalendar, label: string) => {
      const removed = removeLocalBooking(booking.MachineBookingKey);
      if (!removed) return;
      setPending((p) => p + 1);
      try {
        await deleteMachineBooking(booking.MachineBookingKey);
      } catch (err) {
        restoreLocalBooking(removed); // revert optimistic removal
        onError(err instanceof Error ? err.message : 'Failed to delete booking.');
        return;
      } finally {
        setPending((p) => p - 1);
      }
      await reloadConflicts();
      emitScheduleChange({ kind: 'bookings-changed', source: 'machine-schedule' });
      onToast({ message: label });
    },
    [removeLocalBooking, restoreLocalBooking, reloadConflicts, onToast, onError],
  );

  return {
    commitPlacement,
    commitPlacementBatch,
    commitDelete,
    isMutating: pending > 0,
    undo: history.undo,
    redo: history.redo,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
  };
}
